import React, { useRef, useState } from 'react';
import { Form as AntdForm, Modal } from 'antd';
import { Action } from '@triare/auth-redux';
import { Callbacks, Rule } from 'rc-field-form/lib/interface';
import { FormProps as AntdFormProps } from 'rc-field-form/lib/Form';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import clsx from 'clsx';
import { RequiredMark } from 'antd/es/form/Form';
import { useAuth } from '../../../../../store/auth';
import AlertSuccess, { AlertSuccessProps } from './Alert/Success';
import AlertError, { AlertErrorProps } from './Alert/Error';
import Submit from '../../../../Common/Form/Button/Submit';
import Loading from '../../../../Common/Loading';

import styles from '../../index.module.scss';

// Google reCAPTCHA Site Key
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_GG_RECAPTCHA_SITE_KEY || '';

interface FormProps<P> extends AntdFormProps {
  disableAlertSuccess?: true;
  requiredFields?: '*' | string[];
  validationRules: Rule;
  styles?: { [key: string]: string };

  children?: React.ReactNode;
  alertSuccess?: AlertErrorProps;
  alertError?: AlertSuccessProps;

  signUp: (payload: P) => Action | void;
  onFinishFailed?: Callbacks['onFinishFailed'];

  requiredMark?: RequiredMark;
}

function FormSignUp<P>({
  disableAlertSuccess = undefined,
  requiredFields = '*',
  children = undefined,
  alertSuccess = undefined,
  alertError = undefined,
  initialValues = { remember: true },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  },
  validationRules,
  styles: additionalStyles,
  signUp,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  form,
  ...props
}: FormProps<P>): React.ReactNode {
  const { signUp: { loading, response } } = useAuth();
  const { t } = useTranslation();

  // State to control Modal and store form data
  const [isCaptchaVisible, setIsCaptchaVisible] = useState(false);
  const [captchaReady, setCaptchaReady] = useState(false); // New state for loader
  const [pendingFormData, setPendingFormData] = useState<P | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  // 1. Intercept the original submit
  const onFormFinish = (values: P) => {
    setPendingFormData(values);
    setIsCaptchaVisible(true);
    setCaptchaReady(false);
  };

  // 2. Handle Captcha Success
  const onCaptchaChange = (token: string | null) => {
    if (token && pendingFormData) {
      // Create final payload.
      // Note: You likely need to update your Redux types to accept 'recaptchaToken'
      // We cast to 'any' here to avoid TypeScript errors if P is strict
      const payload = {
        ...pendingFormData,
        recaptchaToken: token,
      } as unknown as P;

      signUp(payload);

      // Reset and close
      setIsCaptchaVisible(false);
      setPendingFormData(null);
    }
  };

  const handleModalCancel = () => {
    setIsCaptchaVisible(false);
    setPendingFormData(null);
    setCaptchaReady(false);
    recaptchaRef.current?.reset();
  };

  const handleCaptchaLoaded = () => {
    // Small timeout ensures the Google widget has actually rendered
    // its internal iframe before we hide the loader
    setTimeout(() => setCaptchaReady(true), 300);
  };

  return (
    <>
      <AntdForm
        className={clsx(styles.form, additionalStyles?.form)}
        name="signUp"
        autoComplete="off"
        layout="vertical"
        onFinish={onFormFinish} // Use our interceptor instead of direct signUp
        onFinishFailed={onFinishFailed}
        {...props}
      >
        <div className={styles.alertWrapper}>
          {disableAlertSuccess ? null : (
            <AlertSuccess response={response} {...alertSuccess} />
          )}
          <AlertError {...alertError} />
        </div>

        {children}

        <Submit
          requiredFields={requiredFields}
          className={clsx(styles?.submit, additionalStyles?.submit)}
          loading={loading}
          size="large"
        >
          Sign Up
        </Submit>
      </AntdForm>

      {/* Captcha Modal */}
      <Modal
        open={isCaptchaVisible}
        footer={null}
        onCancel={handleModalCancel}
        centered
        width={350} // Adjust width to fit the captcha
        destroyOnClose
        afterClose={() => setCaptchaReady(false)}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '20px 0',
          minHeight: '118px',
          position: 'relative',
        }}
        >
          {!captchaReady && <Loading absolute />}
          <div style={{ visibility: captchaReady ? 'visible' : 'hidden' }}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={onCaptchaChange}
              asyncScriptOnLoad={handleCaptchaLoaded}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}

export default FormSignUp;
